import { safeNumber } from "./storage";

export type FundingWorksheet = {
  version: 1;
  rows: number;
  columns: number;
  cells: Record<string, string>;
  updatedAt?: string;
};

const MAX_ROWS = 60;
const MAX_COLUMNS = 16;

export const sheetColumnName = (index: number) => {
  let value = Math.max(0, Math.floor(index));
  let result = "";
  do {
    result = String.fromCharCode(65 + (value % 26)) + result;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);
  return result;
};

export const sheetCellRef = (rowIndex: number, columnIndex: number) =>
  `${sheetColumnName(columnIndex)}${rowIndex + 1}`;

const columnIndex = (name: string) => {
  let value = 0;
  for (const char of name.toUpperCase()) value = value * 26 + char.charCodeAt(0) - 64;
  return value - 1;
};

export const normalizeFundingWorksheet = (sheet?: Partial<FundingWorksheet> | null): FundingWorksheet => ({
  version: 1,
  rows: Math.min(MAX_ROWS, Math.max(8, Math.floor(Number(sheet?.rows) || 12))),
  columns: Math.min(MAX_COLUMNS, Math.max(6, Math.floor(Number(sheet?.columns) || 8))),
  cells: sheet?.cells && typeof sheet.cells === "object" ? { ...sheet.cells } : {},
  ...(sheet?.updatedAt ? { updatedAt: String(sheet.updatedAt) } : {}),
});

export const createFundingWorksheet = (
  entries: Array<{ name: string }> = [],
  year = String(new Date().getFullYear()),
): FundingWorksheet => {
  const cells: Record<string, string> = {
    A1: "服务／合同",
    B1: "年度",
    C1: "第一笔",
    D1: "第二笔",
    E1: "第三笔",
    F1: "第四笔",
    G1: "年度合计",
    H1: "备注",
  };
  entries.forEach((entry, index) => {
    const row = index + 2;
    cells[`A${row}`] = entry.name;
    cells[`B${row}`] = year;
    cells[`G${row}`] = `=SUM(C${row}:F${row})`;
  });
  return normalizeFundingWorksheet({
    version: 1,
    rows: Math.max(12, entries.length + 5),
    columns: 8,
    cells,
    updatedAt: new Date().toISOString(),
  });
};

const roundSheetNumber = (value: number) =>
  Math.round((value + Number.EPSILON) * 1000000) / 1000000;

export const evaluateFundingCell = (
  sheet: Pick<FundingWorksheet, "cells">,
  reference: string,
  trail: string[] = [],
): number => {
  const ref = reference.toUpperCase();
  if (trail.includes(ref)) return 0;
  const raw = String(sheet.cells[ref] || "").trim();
  if (!raw.startsWith("=")) return safeNumber(raw.replace(/,/g, ""));
  const nextTrail = [...trail, ref];
  let expression = raw.slice(1).toUpperCase();
  expression = expression.replace(/SUM\(([A-Z]+)(\d+):([A-Z]+)(\d+)\)/g, (_match, fromColumn, fromRow, toColumn, toRow) => {
    const columnStart = Math.min(columnIndex(fromColumn), columnIndex(toColumn));
    const columnEnd = Math.max(columnIndex(fromColumn), columnIndex(toColumn));
    const rowStart = Math.min(Number(fromRow), Number(toRow));
    const rowEnd = Math.max(Number(fromRow), Number(toRow));
    let sum = 0;
    for (let row = rowStart; row <= rowEnd; row += 1) {
      for (let column = columnStart; column <= columnEnd; column += 1) {
        sum += evaluateFundingCell(sheet, `${sheetColumnName(column)}${row}`, nextTrail);
      }
    }
    return String(roundSheetNumber(sum));
  });
  expression = expression.replace(/\b([A-Z]+\d+)\b/g, (cellRef) =>
    String(evaluateFundingCell(sheet, cellRef, nextTrail)));
  if (!/^[0-9+\-*/().\s]+$/.test(expression)) return 0;
  try {
    const result = Function(`"use strict"; return (${expression})`)();
    return typeof result === "number" && Number.isFinite(result) ? roundSheetNumber(result) : 0;
  } catch {
    return 0;
  }
};

export const fundingCellDisplay = (sheet: FundingWorksheet, reference: string) => {
  const raw = String(sheet.cells[reference] || "");
  if (!raw.trim().startsWith("=")) return raw;
  return String(evaluateFundingCell(sheet, reference));
};

export const resizeFundingWorksheet = (
  sheet: FundingWorksheet,
  rows: number,
  columns: number,
): FundingWorksheet => normalizeFundingWorksheet({
  ...sheet,
  rows,
  columns,
  cells: Object.fromEntries(Object.entries(sheet.cells).filter(([reference]) => {
    const match = /^([A-Z]+)(\d+)$/.exec(reference);
    return match && columnIndex(match[1]) < columns && Number(match[2]) <= rows;
  })),
  updatedAt: new Date().toISOString(),
});
